import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { InterviewsListFeatureState } from './interviews-list.models';
import { interviewsListFeature, initialInterviewsListState } from './interviews-list.reducer';
import { getPaginationPage } from './interviews-list.cache';

function shouldClearFromStorage(state: InterviewsListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    filters.studentId === null &&
    (filters.studentLabel === null || filters.studentLabel === '') &&
    filters.fromDate === null &&
    filters.toDate === null &&
    (filters.jobRole === null || filters.jobRole === '') &&
    (filters.companyName === null || filters.companyName === '') &&
    filters.cseAssignedId === null &&
    filters.csiAssignedId === null &&
    (!Array.isArray(filters.interviewRoundTypeIds) || filters.interviewRoundTypeIds.length === 0) &&
    filters.interviewStatusId === null &&
    filters.interviewResultId === null &&
    filters.interviewEnabledById === null;
  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialInterviewsListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;
  const sortDefault =
    state.sort.sortField === '' &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === '' &&
    state.sort.previousSortOrder === 0;
  const noGridData = state.gridData === null;
  return filtersDefault && paginationDefault && sortDefault && noGridData;
}

function parseState(raw: unknown): InterviewsListFeatureState | null {
  if (!raw || typeof raw !== 'object') return null;
  const partial = raw as Partial<InterviewsListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) return null;

  const paginationPartial = partial.pagination as InterviewsListFeatureState['pagination'];
  const rawGridData = partial.gridData ?? null;
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.interviews)
      ? { interviews: rawGridData.interviews }
      : null;

  const parsedPagination: InterviewsListFeatureState['pagination'] = {
    ...initialInterviewsListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialInterviewsListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  const filters = partial.filters as InterviewsListFeatureState['filters'] & {
    interviewRoundTypeId?: string | null;
  };
  // Legacy persisted state stored a single `interviewRoundTypeId` string.
  // Migrate to the new `interviewRoundTypeIds` array shape so older browsers
  // upgrading the bundle don't crash when reading the saved snapshot.
  const legacyRoundTypeId = filters.interviewRoundTypeId;
  const normalizedRoundTypeIds = Array.isArray(filters.interviewRoundTypeIds)
    ? filters.interviewRoundTypeIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : legacyRoundTypeId
      ? [legacyRoundTypeId]
      : [];
  return {
    filters: {
      ...initialInterviewsListState.filters,
      ...filters,
      companyName:
        filters.companyName !== undefined && filters.companyName !== null
          ? filters.companyName
          : initialInterviewsListState.filters.companyName,
      interviewRoundTypeIds: normalizedRoundTypeIds,
      studentLabel:
        filters.studentLabel !== undefined && filters.studentLabel !== null
          ? filters.studentLabel
          : initialInterviewsListState.filters.studentLabel
    },
    pagination: parsedPagination,
    sort: partial.sort,
    gridData: normalizedGridData
  };
}

export const interviewsListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<InterviewsListFeatureState>({
    featureKey: interviewsListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.interviewsList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseState
  })
];
